// ABC SEO/GEO — Block 7: Rank Tracking (populated with 28-day GSC history).
(function () {
  const { Icon } = window.SGIcons;
  const { Card, CardHead, Delta, PageHead } = window.SGKit;

  function StatRow({ stats }) {
    return (
      <Card style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', overflow: 'hidden' }}>
        {stats.map((s, i) => (
          <div key={s.label} style={{ padding: '18px 20px', borderLeft: i ? '1px solid var(--sg-border)' : 'none' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--sg-text-2)', marginBottom: 8 }}>{s.label}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="sg-tnum" style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 700, fontSize: 26, letterSpacing: '-0.02em', color: s.accent || 'var(--sg-text)', lineHeight: 1 }}>{s.value}</span>
              {s.delta && <Delta dir={s.dir}>{s.delta}</Delta>}
            </div>
          </div>
        ))}
      </Card>
    );
  }

  /* average-position trend (inverted: up = better) — real 28-day daily series
     from Search Console. Shows an empty state until enough history exists. */
  function RankTrend({ series }) {
    const data = Array.isArray(series) ? series : [];
    const liveBadge = <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--sg-green)', background: 'var(--sg-green-50)', padding: '3px 10px', borderRadius: 'var(--sg-radius-pill)' }}>● LIVE</span>;
    if (data.length < 2) {
      return (
        <Card>
          <CardHead title="Average position — 28 days" sub="Daily Google rank from Search Console · higher line = better positions" right={liveBadge} />
          <div style={{ padding: '22px 20px', fontSize: 13, color: 'var(--sg-text-2)' }}>Not enough daily history yet — your average-position trend will appear here as Google records more days.</div>
        </Card>
      );
    }
    const W = 760, H = 240, padX = 16, padT = 22, padB = 28;
    const lo = Math.min(...data), hi = Math.max(...data), pad = (hi - lo) * 0.15 || 1;
    const dmin = lo - pad, dmax = hi + pad; // position domain
    const x = (i) => padX + (i * (W - padX * 2)) / (data.length - 1);
    const y = (v) => padT + ((v - dmin) / (dmax - dmin)) * (H - padT - padB); // higher position number -> lower on chart
    const line = data.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
    return (
      <Card>
        <CardHead title="Average position — 28 days" sub="Daily Google rank from Search Console · higher line = better positions" right={liveBadge} />
        <div style={{ padding: '14px 16px 4px', position: 'relative' }}>
          <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="240" preserveAspectRatio="none" style={{ display: 'block' }}>
            <defs><linearGradient id="sgRankA" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563EB" stopOpacity="0.16" /><stop offset="100%" stopColor="#2563EB" stopOpacity="0" /></linearGradient></defs>
            {[0, 1, 2, 3].map((g) => <line key={g} x1={padX} x2={W - padX} y1={padT + g * (H - padT - padB) / 3} y2={padT + g * (H - padT - padB) / 3} stroke="var(--sg-border)" strokeWidth="1" strokeDasharray="3 4" />)}
            <path d={`${line} L${x(data.length - 1)},${H - padB} L${x(0)},${H - padB} Z`} fill="url(#sgRankA)" />
            <path d={line} fill="none" stroke="var(--sg-blue-500)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
            <circle cx={x(data.length - 1)} cy={y(data[data.length - 1])} r="5" fill="var(--sg-blue-500)" stroke="#fff" strokeWidth="2.5" />
          </svg>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 14px 10px', fontSize: 11, color: 'var(--sg-text-3)' }}>
            <span>28d ago</span><span>18d</span><span>9d</span><span>Today</span>
          </div>
        </div>
      </Card>
    );
  }

  /* One of the client's CHOSEN keywords: real Google position + movement since
     the last check, with clicks/impressions/CTR from Search Console. */
  const GRID = '1fr 86px 84px 80px 84px 72px';
  function TrackedRow({ r, last }) {
    const up = r.change != null && r.change > 0;
    const down = r.change != null && r.change < 0;
    return (
      <div style={{ display: 'grid', gridTemplateColumns: GRID, alignItems: 'center', gap: 12, padding: '13px 20px', borderBottom: last ? 'none' : '1px solid var(--sg-border)' }}>
        <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--sg-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.kw}</span>
        {/* A checked keyword that isn't in the top 100 is NOT the same as one we
            haven't looked at yet — say which. */}
        <span className="sg-tnum" title={r.pos == null ? (r.checked ? 'Checked — not in Google’s top 100' : 'Not checked yet') : undefined}
          style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 700, fontSize: r.pos == null ? 11.5 : 15, color: r.pos == null ? 'var(--sg-text-3)' : 'var(--sg-text)' }}>
          {r.pos != null ? Math.round(r.pos) : (r.checked ? '100+' : '—')}
        </span>
        <span className="sg-tnum" style={{ fontSize: 12.5, fontWeight: 700, color: up ? 'var(--sg-green)' : down ? 'var(--sg-red, #DC2626)' : 'var(--sg-text-3)' }}>
          {r.change == null || r.change === 0 ? '–' : (up ? '▲ ' + r.change : '▼ ' + Math.abs(r.change))}
        </span>
        <span className="sg-tnum" style={{ fontSize: 13, color: 'var(--sg-text-2)' }}>{r.volume != null ? r.volume.toLocaleString() : '—'}</span>
        <span className="sg-tnum" style={{ fontSize: 13, fontWeight: 700, color: 'var(--sg-text)' }}>{r.clicks != null ? r.clicks.toLocaleString() : '—'}</span>
        <span className="sg-tnum" style={{ fontSize: 13, color: 'var(--sg-text-2)' }}>{r.ctr != null ? (r.ctr * 100).toFixed(1) + '%' : '—'}</span>
      </div>
    );
  }

  function SiteSelect({ sites, site, setSite, prettySite }) {
    return (
      <select value={site} onChange={(e) => setSite(e.target.value)}
        style={{ height: 42, padding: '0 14px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)',
          background: 'var(--sg-card)', fontFamily: 'var(--sg-font-sans)', fontSize: 13.5, fontWeight: 600, color: 'var(--sg-text)', cursor: 'pointer' }}>
        {!sites && <option>Loading sites…</option>}
        {sites && sites.map((s) => <option key={s} value={s}>{prettySite(s)}</option>)}
      </select>
    );
  }

  function RankTracking() {
    const { sites, site, setSite, perf, prettySite } = window.SGLive.useLive();
    const t = window.SGLive.transforms;
    const ent = window.SGLive.useEntitlement();
    const trialMode = ent.status === 'trial';
    const [shown, setShown] = React.useState(25); // paid: page 25 at a time
    // The keywords the CLIENT CHOSE (approved) + their latest real SERP position.
    // This screen tracks exactly that set — not every query Google happens to show.
    const [tracked, setTracked] = React.useState(null);
    const [checking, setChecking] = React.useState(false);
    const [checkMsg, setCheckMsg] = React.useState(null);
    const loadTracked = React.useCallback(() => window.SGLive.fetchTrackedKeywords().then(setTracked), []);
    React.useEffect(() => {
      setShown(25); setTracked(null); setCheckMsg(null);
      loadTracked();
    }, [site, loadTracked]);
    // Look up every tracked keyword's live Google position right now. Needed for
    // keywords added before the automatic check, and any time you want it fresh.
    const recheck = () => {
      if (checking) return;
      setChecking(true); setCheckMsg(null);
      window.SGLive.checkRanks().then((r) => {
        setChecking(false);
        if (r && r.ok) {
          // Each Google lookup takes several seconds, so the bulk runs in the
          // background — tell them that instead of showing a silent "0 checked".
          setCheckMsg({ ok: true, text: r.queued
            ? `Checking ${r.queued} keyword${r.queued === 1 ? '' : 's'} against Google in the background — positions appear here over the next few minutes. Refresh to see them land.`
            : `Checked ${r.checked} keyword${r.checked === 1 ? '' : 's'} — found ${r.found} ranking in Google's top 100.` });
          loadTracked();
          // Pick up background results without needing a manual refresh.
          if (r.queued) { setTimeout(loadTracked, 20000); setTimeout(loadTracked, 60000); }
        } else setCheckMsg({ ok: false, text: (r && r.error) || 'Could not check rankings right now.' });
      });
    };

    // Shared merge (SGLive.transforms) — the Keywords screen uses the exact same
    // helpers, so the two screens can never show different numbers.
    const rows = React.useMemo(() => t.trackedRows(tracked, perf), [tracked, perf, t]);
    const sum = React.useMemo(() => t.trackedSummary(rows), [rows, t]);
    const liveStats = rows ? [
      { label: 'Keywords tracked', value: String(sum.total) },
      { label: 'Top 3', value: String(sum.top3), accent: 'var(--sg-green)' },
      { label: 'Top 10', value: String(sum.top10) },
      { label: 'Avg. position', value: sum.avgPos != null ? sum.avgPos.toFixed(1) : '—' },
      { label: 'Improved', value: String(sum.improved), accent: sum.improved ? 'var(--sg-green)' : undefined },
    ] : null;
    const live = !!rows && rows.length > 0;
    return (
      <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <PageHead eyebrow="Research" title="Rank Tracking" icon="list-ordered"
          sub={live ? `Google positions for the ${rows.length} keyword${rows.length === 1 ? '' : 's'} you chose for ${prettySite(site)}.` : 'Tracks the keywords you choose — their Google position, movement and clicks.'}
          right={live ? (
            <button onClick={recheck} disabled={checking}
              title="Look up every tracked keyword's current Google position now"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 38, padding: '0 16px', border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: checking ? 'wait' : 'pointer', opacity: checking ? 0.65 : 1, fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13, color: '#fff', background: 'var(--sg-grad-brand)' }}>
              <Icon name="refresh-cw" size={14} /> {checking ? 'Checking Google…' : 'Check rankings now'}
            </button>
          ) : null} />
        {checkMsg && (
          <Card style={{ padding: '12px 16px', fontSize: 13, color: checkMsg.ok ? 'var(--sg-text)' : 'var(--sg-red, #DC2626)', background: checkMsg.ok ? 'var(--sg-green-50)' : 'var(--sg-red-50, #FDECEC)', border: 'none' }}>{checkMsg.text}</Card>
        )}
        {rows === null ? (
          <Card style={{ padding: 40, textAlign: 'center', fontSize: 13.5, color: 'var(--sg-text-2)' }}>Loading your tracked keywords…</Card>
        ) : !live ? (
          <Card style={{ padding: '56px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <span style={{ width: 60, height: 60, borderRadius: 16, background: 'var(--sg-blue-50)', color: 'var(--sg-blue-600)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="list-ordered" size={28} /></span>
            <div style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 600, fontSize: 20, color: 'var(--sg-text)' }}>No keywords chosen yet</div>
            <div style={{ fontSize: 13.5, color: 'var(--sg-text-2)', maxWidth: 470, lineHeight: 1.55 }}>This screen tracks the keywords <strong>you pick</strong>. Add some in <strong>Site Audit → Keywords to target</strong>, or in the <strong>Keywords</strong> tab — we’ll check their Google position right away and follow them from then on.</div>
          </Card>
        ) : (
        <React.Fragment>
        <StatRow stats={liveStats} />
        <RankTrend series={t.posSeries(perf)} />
        <Card>
            <React.Fragment>
              <CardHead title="Your keywords" sub={(() => {
                const last = Math.max(0, ...rows.map((r) => r.checked || 0));
                const when = last ? new Date(last * 1000).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : null;
                return `The keywords you chose for ${prettySite(site)} · ${sum.ranked} of ${sum.total} rank in the top 100${when ? ' · last checked ' + when : ''}`;
              })()}
                right={<span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--sg-green)', background: 'var(--sg-green-50)', padding: '3px 10px', borderRadius: 'var(--sg-radius-pill)' }}>● LIVE</span>} />
              <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 12, padding: '10px 20px', borderBottom: '1px solid var(--sg-border)', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--sg-text-3)' }}>
                <span>Keyword</span><span>Position</span><span>Change</span><span>Volume</span><span>Clicks</span><span>CTR</span>
              </div>
              {(() => {
                const cap = trialMode ? 25 : shown;
                const visible = rows.slice(0, cap);
                return visible.map((r, i) => <TrackedRow key={i} r={r} last={i === visible.length - 1} />);
              })()}
              {/* trial cap note / paged "show more" */}
              {trialMode && rows.length > 25 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '13px 20px', borderTop: '1px solid var(--sg-border)', background: 'var(--sg-amber-50)' }}>
                  <Icon name="zap" size={16} color="var(--sg-amber)" />
                  <div style={{ flex: 1, fontSize: 12.5, color: 'var(--sg-text)' }}><strong>Trial:</strong> showing 25 of {rows.length} ranking keywords. Upgrade to track them all.</div>
                  <button onClick={() => { try { window.parent && window.parent.postMessage({ type: 'abc-upgrade' }, '*'); } catch (e) { /* noop */ } }} style={{ flex: 'none', height: 34, padding: '0 14px', border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: 'pointer', fontWeight: 700, fontSize: 13, color: '#fff', background: 'var(--sg-grad-brand)' }}>Upgrade</button>
                </div>
              ) : (!trialMode && shown < rows.length && (
                <button onClick={() => setShown((n) => n + 25)} style={{ width: '100%', padding: '13px 20px', borderTop: '1px solid var(--sg-border)', border: 'none', borderBottom: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13, color: 'var(--sg-blue-600)' }}>
                  Show next {Math.min(25, rows.length - shown)} ({rows.length - shown} more)
                </button>
              ))}
            </React.Fragment>
        </Card>
        </React.Fragment>
        )}
      </div>
    );
  }

  window.SGRankTracking = RankTracking;
})();
